import type { Pattern } from '../pipeline/pipeline';

/**
 * Flood fill 4-voisinage (haut, bas, gauche, droite — pas en diagonale).
 *
 * Retourne la liste des cellules `(col, row)` connexes à `(startCol, startRow)`
 * partageant la même valeur d'index de couleur. Utilisé pour l'outil "pot de
 * peinture" : un seul clic remplit toute la zone homogène.
 *
 * Implémentation BFS avec queue à index croissant (évite l'O(n) de `Array.shift`)
 * et un `Set<number>` de cellules déjà visitées. Complexité O(N_zone), pas O(N_grille).
 *
 * @throws si `(startCol, startRow)` est hors-bornes.
 */
export function floodFill(
  pattern: Pattern,
  startCol: number,
  startRow: number,
): Array<{ col: number; row: number }> {
  if (
    startCol < 0 ||
    startCol >= pattern.width ||
    startRow < 0 ||
    startRow >= pattern.height
  ) {
    throw new Error(
      `floodFill : (${startCol}, ${startRow}) hors bornes de la grille ${pattern.width}×${pattern.height}.`,
    );
  }

  const startIdx = pattern.cells[startRow * pattern.width + startCol];
  if (startIdx === undefined) return [];

  const result: Array<{ col: number; row: number }> = [];
  const visited = new Set<number>();
  const queue: Array<{ col: number; row: number }> = [{ col: startCol, row: startRow }];
  let head = 0;

  while (head < queue.length) {
    const current = queue[head++];
    if (current === undefined) continue;
    const { col, row } = current;

    // Bornes
    if (col < 0 || col >= pattern.width || row < 0 || row >= pattern.height) continue;
    const key = row * pattern.width + col;
    if (visited.has(key)) continue;
    const idx = pattern.cells[key];
    if (idx !== startIdx) continue;

    visited.add(key);
    result.push({ col, row });

    queue.push({ col: col + 1, row });
    queue.push({ col: col - 1, row });
    queue.push({ col, row: row + 1 });
    queue.push({ col, row: row - 1 });
  }

  return result;
}
